import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Container, Row, Col } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

const UUIDGenerator = () => {
  const {
    register,
    handleSubmit,
    errors,
    formState: { isSubmitting, isDirty, isValid },
  } = useForm({ mode: "onChange" });
  const [uuid, setUUID] = useState(null);
  const [error, setError] = useState(null);

  const onSubmit = () => {
    const crypto = window.crypto || window.msCrypto;
    if (!crypto) {
      setError("Crypto API not available.");
    } else {
      const buffer = new Uint8Array(16);
      crypto.getRandomValues(buffer);

      const uuid = Array.from(buffer)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")
        .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, "$1-$2-$3-$4-$5");

      setUUID(uuid);
    }
  };

  const handleCopy = (data) => {
    navigator.clipboard.writeText(data);
    toast.success("Copied.");
  };

  const faqs = [
    {
      title: "What is UUID?",
      desc: "UUID (Universally Unique Identifier) is a 128-bit identifier used to uniquely identify information without central coordination. It ensures uniqueness across devices and time, making it valuable for various applications like databases, distributed systems, and generating unique keys for data records.",
    },
    {
      title: "What is a UUID used for?",
      desc: "UUIDs are a useful tool for generating unique identifiers that can be used in a variety of contexts, including primary keys for database record identification, messaging and communication, security and authentication, distributed systems, testing and debugging, and more.",
    },
    {
      title: "Is this generator free?",
      desc: "Yes, this tool is completely free and made by App Upgrade Team. Feel free to bookmark this page.",
    },
    {
      title: "Is my data safe?",
      desc: "We don't store the data entered by you. The generation takes place in your browser locally.",
    },
  ];

  return (
    <section className="landing-page-section bg-landing-page">
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-xl-8 col-lg-8 col-md-8">
            <div className="section-title text-center title-ex1 mb-4">
              <div>
                <p>
                  <Link className="" to={"/utilities"}>
                    ← All Developer Utilities
                  </Link>{" "}
                </p>
                <h2 className="light-purple-gradient">UUID Generator</h2>
              </div>

              <p
                className="landing-muted-text"
                style={{ fontSize: "large", fontWeight: "normal" }}
              >
                Create a universally unique identifier (UUID) using this free
                tool by App Upgrade.
              </p>
            </div>
          </div>

          <Container>
            <Row className="dark-mode justify-content-center mb-4">
              <Col lg={12} md={8} className="">
                <form
                  className="form justify-content-center"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="d-flex mt-1 justify-content-center">
                    <div className="form-group">
                      <button
                        className="btn btn-danger btn-block gradient-bg-accent"
                        type="submit"
                        disabled={!isDirty && !isValid}
                      >
                        {isSubmitting ? "Generating UUID..." : "Generate UUID"}
                      </button>
                    </div>
                  </div>
                </form>
                {error && <p className="error">{error}</p>}
                <div className="form">
                  <label className="d-flex justify-content-center text-cool-black text-center">
                    UUID{" "}
                  </label>
                  <div className="form-row justify-content-center">
                    <div className="form-group col-md-6">
                      <input
                        name="value"
                        className="form-control text-center"
                        rows="4"
                        defaultValue={uuid}
                      />
                    </div>
                  </div>
                  <div className="text-center">
                    <button
                      className="btn outline-text gradient-bg-accent"
                      onClick={() => handleCopy(uuid)}
                      disabled={!uuid}
                    >
                      <div className="d-flex align-items-center gap">
                        <FontAwesomeIcon className="fa-thin" icon={faCopy} />
                        <span class="pl-2">Copy to Clipboard</span>
                      </div>
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              {faqs.map((faq, key) => (
                <Col className="mt-10" key={key} lg={6} md={6}>
                  <div style={{ textAlign: "left" }}>
                    <div className="d-flex justify-content-start">
                      <div
                        style={{ marginBottom: "0px", paddingRight: "4px" }}
                      ></div>
                      <h5 className="text-white">{faq.title}</h5>
                    </div>

                    <p
                      className="landing-muted-text mb-4"
                      style={{ fontSize: "1rem" }}
                    >
                      {faq.desc}
                    </p>
                  </div>
                </Col>
              ))}
            </Row>
            <Row className="justify-content-center mt-10 mb-4">
              <Col lg={12} md={8} className="">
                <div className="title text-center m-4">
                  <h1 className="font-weight-bolder line-height-1_4 text-white">
                    Easily{" "}
                    <span className="light-purple-gradient">force update</span>{" "}
                    your mobile apps.
                  </h1>
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col lg={6} md={8}>
                <div className="title text-center mb-5">
                  <span
                    className="text-white text-landing-h1"
                    style={{ fontSize: "2.0rem" }}
                  >
                    Start with{" "}
                    <span className="light-purple-gradient">App Upgrade</span>,
                    today.
                  </span>
                  <p
                    className="light-purple-gradient mt-3"
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "normal",
                      color: "rgba(144, 97, 249,1)",
                    }}
                  >
                    Start with <b>free 15 days</b> trial. No credit card
                    required.
                  </p>
                </div>
                <div
                  className="mt-5"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <a
                      target="_blank"
                      href="https://appupgrade.dev/signup"
                      className="btn btn-cta btn-full-width outline-text glowing outline-shadow"
                      rel="noreferrer"
                    >
                      Get started →
                    </a>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <a
                      target="_blank"
                      href="mailto:support@appupgrade.dev"
                      className="btn btn-cta btn-full-width outline-text glowing outline-shadow"
                      rel="noreferrer"
                      style={{ padding: ".375rem .75rem" }}
                    >
                      Contact us →
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </section>
  );
};

export default UUIDGenerator;
